import React, { useState, useRef, useEffect, useContext } from 'react';
import { useChatPanel } from './ChatPanelContext';
import Sidebar from './sidebar/Sidebar';
import ChannelMembersModal from './ChannelMembersModal';
import './ChatPanel.css';
import useApi from '../../../utils/api-manager/Helper/useApi';
import AuthContext from "../../../utils/secure-route/AuthContext";
import MailApp from './Mail/MailApp';

const getFreeAvatarUrl = (seed = 'user') =>
  `https://api.dicebear.com/9.x/avataaars/svg?seed=${encodeURIComponent(seed)}&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf`;

const ChatPanel = () => {
  const {
    isOpen, isMinimized, isEdgeMode, showEdgeStrip, closePanel, minimizePanel, restorePanel, closeEdgeMode, openFromEdgeMode, isMaximized, toggleMaximizePanel
  } = useChatPanel();
  const { Get, Post, getAPI, Patch } = useApi();
  const { user } = useContext(AuthContext);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const storeId = user?.store;
  const currentUserId = user?.user_id;

  // console.log(user)
  const [channels, setChannels] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Welcome to the chat! Create a channel or start a conversation to get started.",
      sender: 'bot',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      channel: 'welcome'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [activeChannel, setActiveChannel] = useState('welcome');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState('main');
  const [isDMSearchActive, setIsDMSearchActive] = useState(false);
  const [dmSearchQuery, setDmSearchQuery] = useState('');
  const [selectedDMs, setSelectedDMs] = useState([]);
  const [groups, setGroups] = useState([]);
  const [directMessages, setDirectMessages] = useState([]);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const [channelCount, setChannelCount] = useState(0);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [emails, setEmails] = useState([]);
  const [currentUser, setCurrentUser] = useState({
    id: currentUserId || 'current-user',
    name: 'Current User',
    status: 'online'
  });

  const [isChannelMembersModalOpen, setIsChannelMembersModalOpen] = useState(false);
  const [isMailMode, setIsMailMode] = useState(false);

  const [workspaces] = useState([
    { id: 'main', name: 'My Workspace', plan: 'Free plan', active: true },
  ]);
  const filteredEmails = emails.filter(email =>
    email.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
    email.sender.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleEmailClick = (email) => {
    setSelectedEmail(email);
  };

  useEffect(() => {
    if (isOpen) fetchChannels();
  }, [isOpen]);

  const fetchChannels = async (url = "channel") => {
    try {
      const response = await Get(url);
      if (response?.results) {
        const formatted = response.results.map(channel => ({
          id: channel.id,
          name: channel.title,
          members: channel.members,
          members_name: channel.members_name,
          createdAt: channel.created_at
        }));

        setChannels(formatted);
        // console.log(formatted)
        setChannelCount(response.count || 0);

        if (formatted.length > 0 && activeChannel === 'welcome') {
          setActiveChannel(formatted[0].id);
          if (currentUserId) {
            fetchChannelMessages(formatted[0].id, currentUserId);
          }
        }
      }
    } catch (err) {
      console.error("Failed to fetch channels:", err);
    }
  };

  const hasMountedRef = useRef(false);

  useEffect(() => {
    fetchUsers();
    hasMountedRef.current = true;
  }, []);

  useEffect(() => {
    if (isOpen && hasMountedRef.current) {
      fetchUsers();
    }
  }, [isOpen]);

  async function fetchUsers() {
    try {
      const apiResponse = await Get("getChatUsers")
      const transformedUsers = apiResponse.users.map(user => ({
        id: String(user.id),
        name: `${user.first_name} ${user.last_name}`.trim(),
        email: user.email || '',
        status: 'online',
        avatar: user.avatar || null
      }));

      setDirectMessages(transformedUsers);
      setAvailableUsers(transformedUsers);

      const matchedUser = transformedUsers.find(u => String(u.id) === String(currentUserId));
      if (matchedUser) {
        setCurrentUser({
          id: matchedUser.id,
          name: matchedUser.name,
          status: matchedUser.status,
        });
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  }

  const fetchDMMessages = async (dmId, dmFromId, dmToId) => {
    if (!dmId || !dmFromId || !dmToId) {
      console.error('Missing required parameters for fetching DM messages');
      console.error('Parameters received:', { dmId, dmFromId, dmToId });
      return;
    }
    if (channels.find(c => c.id === dmId)) {
      return;
    }
    setIsLoadingMessages(true);
    try {
      const apiUrl = `${getAPI("getChat")}?message_from=${dmFromId}&message_to=${dmToId}`;
      const response = await Get(apiUrl)
      if (response && Array.isArray(response)) {
        const transformedMessages = response.map(msg => {
          const isCurrentUserSender = String(msg.message_from) === String(dmFromId);
          return {
            id: msg.id,
            text: msg.message,
            sender: isCurrentUserSender ? 'user' : 'other',
            senderName: isCurrentUserSender ? msg.message_from_name : msg.message_from_name,
            timestamp: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            channel: dmId,
            hasRead: msg.has_read_message,
            createdAt: msg.created_at
          };
        });

        transformedMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        console.log('Transformed messages:', transformedMessages);
        setMessages(prev => {
          const otherMessages = prev.filter(msg => msg.channel !== dmId);
          return [...otherMessages, ...transformedMessages];
        });
      } else {
        console.log('No messages found for this conversation');
        setMessages(prev => prev.filter(msg => msg.channel !== dmId));
      }
    } catch (error) {
      console.error('Failed to fetch DM messages:', error);
      const errorMessage = {
        id: Date.now(),
        text: "Failed to load messages. Please try again.",
        sender: 'bot',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        channel: dmId
      };
      setMessages(prev => [...prev.filter(msg => msg.channel !== dmId), errorMessage]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const selectedWorkspace = workspaces.find(w => w.id === selectedWorkspaceId);

  const fetchChannelMessages = async (channelId, userId) => {
    if (!channelId || !userId) {
      console.error("Missing channelId or userId");
      return;
    }

    setIsLoadingMessages(true);
    try {
      const apiUrl = `${getAPI("getChannelChat")}?channel_id=${channelId}&user_id=${userId}`;

      const response = await Get(apiUrl);
      // console.log(response)
      if (response && Array.isArray(response)) {
        const transformedMessages = response.map(msg => ({
          id: msg.id,
          text: msg.message,
          sender: String(msg.message_from) === String(userId) ? 'user' : 'other',
          senderName: msg.message_from_name,
          timestamp: new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          channel: channelId,
          hasRead: msg.has_read_message,
          createdAt: msg.created_at
        }));

        transformedMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        setMessages(prev => {
          const otherMessages = prev.filter(msg => msg.channel !== channelId);
          return [...otherMessages, ...transformedMessages];
        });
      } else {
        setMessages(prev => prev.filter(msg => msg.channel !== channelId));
      }
    } catch (error) {
      console.error('Failed to fetch channel messages:', error);

      // Create centered welcome message
      const welcomeMessage = {
        id: Date.now(),
        text: "Welcome! You don't have access to this channel. Please contact the channel admin for access.",
        sender: 'welcome', // Special type for welcome messages
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        channel: channelId
      };

      setMessages(prev => [...prev.filter(msg => msg.channel !== channelId), welcomeMessage]);
    } finally {
      setIsLoadingMessages(false);
    }
  };

  const handleSendMessage = async () => {
    if (inputValue.trim()) {

      if (activeChannel === 'welcome') {
        return;
      }

      const newMessage = {
        id: Date.now(),
        text: inputValue,
        sender: 'user',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        channel: activeChannel
      };

      setMessages(prev => [...prev, newMessage]);
      const messageText = inputValue;
      setInputValue('');

      const isChannel = channels.find(c => c.id === activeChannel);
      const isDM = directMessages.find(dm => dm.id === activeChannel);

      if (!currentUserId) {
        console.error("Current user ID not found");
        setIsTyping(false);
        return;
      }

      try {
        if (isChannel) {
          const channelPayload = {
            store: storeId,
            channel_id: isChannel.id,
            message_from: currentUserId,
            message_from_name: currentUser.name,
            message: messageText
          };
          console.log("Sending channel message payload:", channelPayload);
          const response = await Post('channelChat', channelPayload);
          const data = response.data;
          console.log('Channel message sent successfully:', data);

        } else if (isDM) {
          const dmPayload = {
            store: storeId,
            message_to: isDM.id,
            message_to_name: isDM.name,
            message_from: currentUserId,
            message_from_name: currentUser.name,
            message: messageText
          };
          console.log("Sending DM message payload:", dmPayload);
          const response = await Post('chat', dmPayload);
          const data = response.data;

        } else {
          console.error('No valid recipient found for active channel:', activeChannel);
        }
      } catch (error) {
        console.error('Error sending message:', error);
      }

      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleChannelClick = (channelId, channelName) => {
    setActiveChannel(channelId);
    setIsDMSearchActive(false);
    setDmSearchQuery('');
    setSelectedDMs([]);

    const dm = directMessages.find(dm => dm.id === channelId);
    if (dm) {
      const dmFromId = String(currentUserId);
      const dmToId = String(dm.id);
      fetchDMMessages(channelId, dmFromId, dmToId);
    } else {
      const channel = channels.find(c => c.id === channelId);
      if (channel && currentUserId) {
        fetchChannelMessages(channel.id, currentUserId);
      }
    }
  };

  const handleToggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMinimize = () => {
    if (isEdgeMode) {
      minimizePanel();
    } else {
      closeEdgeMode();
    }
  };

  const handleClose = () => {
    if (isEdgeMode) {
      closeEdgeMode();
    } else {
      closePanel();
    }
  };

  const getStatusClass = (status) => {
    const statusClasses = {
      online: 'status-online',
      offline: 'status-offline'
    };
    return statusClasses[status] || statusClasses.offline;
  };

  const getChannelDisplayName = (channelId) => {
    if (channelId === 'welcome') return 'Welcome';

    const channel = channels.find(c => c.id === channelId);
    const dm = directMessages.find(d => d.id === channelId);
    const group = groups.find(g => g.id === channelId);

    if (channel) return channel.name;
    if (dm) return `@${dm.name}`;
    if (group) return group.name;

    return channelId;
  };

  const handleDMSelect = (dm) => {
    setSelectedDMs(prev => {
      const isAlreadySelected = prev.find(selected => selected.id === dm.id);
      if (isAlreadySelected) {
        return prev.filter(selected => selected.id !== dm.id);
      } else {
        return [...prev, dm];
      }
    });
  };

  const handleRemoveSelectedDM = (dmId) => {
    setSelectedDMs(prev => prev.filter(dm => dm.id !== dmId));
  };

  const handleStartConversation = () => {
    if (selectedDMs.length === 0) return;

    if (selectedDMs.length === 1) {
      const selectedDM = selectedDMs[0];
      setActiveChannel(selectedDM.id);

      if (currentUserId) {
        const dmFromId = String(currentUserId);
        const dmToId = String(selectedDM.id);
        fetchDMMessages(selectedDM.id, dmFromId, dmToId);
      }
    } else {
      const sortedMemberIds = selectedDMs.map(dm => dm.id).sort();
      const groupId = `group-${sortedMemberIds.join('-')}`;

      const existingGroup = groups.find(g => g.id === groupId);

      if (!existingGroup) {
        const newGroup = {
          id: groupId,
          name: selectedDMs.map(dm => dm.name).join(', '),
          members: selectedDMs,
          memberIds: sortedMemberIds,
          createdAt: new Date().toISOString(),
          isActive: true
        };

        setGroups(prev => [...prev, newGroup]);
      }

      setActiveChannel(groupId);
    }

    setIsDMSearchActive(false);
    setDmSearchQuery('');
    setSelectedDMs([]);
  };

  const handleCancelDMSelection = () => {
    setIsDMSearchActive(false);
    setDmSearchQuery('');
    setSelectedDMs([]);
  };

  const handleOpenChannelMembersModal = () => {
    const currentChannel = channels.find(c => c.id === activeChannel);
    if (currentChannel) {
      setIsChannelMembersModalOpen(true);
    }
  };

  const handleCloseChannelMembersModal = () => {
    setIsChannelMembersModalOpen(false);
  };

  const handleAddMember = async (user) => {
    try {

      const currentChannel = channels.find(channel => channel.id === activeChannel);
      if (!currentChannel) {
        console.error("Channel not found");
        return;
      }

      const alreadyMember = currentChannel.members?.some(id => String(id) === String(user.id));
      if (alreadyMember) {
        console.warn("User is already a member:", user);
        return;
      }

      const updatedMembers = [...(currentChannel.members || []), user.id];
      const updatedMembersName = [...(currentChannel.members_name || []), user.name];

      const payload = {
        members: updatedMembers,
        members_name: updatedMembersName
      };

      console.log("Adding member payload:", payload);

      const response = await Patch("channel", activeChannel, payload);

      console.log("Add member response:", response);

      if (response && (response.status === 200 || response.data)) {
        const updatedChannel = response.data || response;

        setChannels(prev => prev.map(channel => {
          if (channel.id === activeChannel) {
            return {
              ...channel,
              members: updatedChannel.members || updatedMembers,
              members_name: updatedChannel.members_name || updatedMembersName
            };
          }
          return channel;
        }));

        await fetchChannels();

        // console.log("Member added successfully:", user);
      } else {
        console.error("Failed to add member - invalid response:", response);
      }
    } catch (error) {
      console.error("Failed to add member:", error);
    }
  };

  const handleRemoveMember = async (memberId) => {
    try {

      const currentChannel = channels.find(channel => channel.id === activeChannel);
      if (!currentChannel) {
        console.error("Channel not found");
        return;
      }

      const updatedMembers = (currentChannel.members || []).filter(id => String(id) !== String(memberId));
      const memberToRemove = availableUsers.find(u => String(u.id) === String(memberId));
      const updatedMembersName = (currentChannel.members_name || []).filter(name =>
        memberToRemove ? name !== memberToRemove.name : true
      );

      const payload = {
        members: updatedMembers,
        members_name: updatedMembersName
      };

      // console.log("Removing member payload:", payload);
      const response = await Patch("channel", activeChannel, payload);
      // console.log("Remove member response:", response);
      if (response && (response.status === 200 || response.data)) {

        setChannels(prev => prev.map(channel => {
          if (channel.id === activeChannel) {
            return {
              ...channel,
              members: updatedMembers,
              members_name: updatedMembersName
            };
          }
          return channel;
        }));

        await fetchChannels();

        // console.log("Member removed successfully:", memberId);
      } else {
        console.error("Failed to remove member - invalid response:", response);
      }
    } catch (error) {
      console.error("Failed to remove member:", error);
    }
  };

  const filteredMessages = messages.filter(msg => msg.channel === activeChannel);

  const getCurrentChannelInfo = () => {
    if (activeChannel === 'welcome') {
      return { name: 'Welcome', isWelcome: true };
    }

    const channel = channels.find(c => c.id === activeChannel);
    const dm = directMessages.find(d => d.id === activeChannel);
    const group = groups.find(g => g.id === activeChannel);

    if (group) {
      return {
        name: group.name,
        isGroup: true,
        memberCount: group.members.length
      };
    }

    return channel || dm;
  };

  const currentChannelInfo = getCurrentChannelInfo();

  const getAvatarInitial = (name) => {
    return name ? name.charAt(0).toUpperCase() : '?';
  };

  const getMessageAvatarSeed = (message) => {
    if (message.sender === "user") return currentUser?.name || "Current User";
    if (message.sender === "other") return message.senderName || "Team Member";
    return "PaisaVara Assistant";
  };

  if (showEdgeStrip) {
    return (
      <div className="chat-edge-panel" onClick={openFromEdgeMode}>
        <div className="edge-handle">
          <span>💬</span>
          <span>CHAT</span>
        </div>
      </div>
    );
  }

  if (isMinimized) {
    return (
      <div className="chat-minimized-panel" onClick={restorePanel}>
        <div className="minimized-handle">
          <span>💬</span>
          <span>CHAT</span>
        </div>
      </div>
    );
  }

  if (!isOpen && !isEdgeMode) {
    return null;
  }

  return (
    <>
      <div className="chat-focus-backdrop" onClick={handleClose}></div>
      <div
        className={`chat-panel ${isOpen ? "open" : ""
          } ${isEdgeMode ? "edge-mode" : ""} ${isMaximized ? "maximized" : ""}`}
      >
      <Sidebar
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        setSelectedWorkspaceId={setSelectedWorkspaceId}
        channels={channels}
        directMessages={directMessages}
        groups={groups}
        setChannels={setChannels}
        fetchChannels={fetchChannels}
        activeChannel={activeChannel}
        sidebarCollapsed={sidebarCollapsed}
        isMailMode={isMailMode}
        setIsMailMode={setIsMailMode}
        onToggleCollapse={handleToggleSidebar}
        onChannelClick={handleChannelClick}
        currentUser={currentUser}
        onStartDirectMessage={() => setIsDMSearchActive(true)}
        availableUsers={availableUsers}
      />

      <ChannelMembersModal
        isOpen={isChannelMembersModalOpen}
        onClose={handleCloseChannelMembersModal}
        currentChannel={currentChannelInfo}
        availableUsers={availableUsers}

        onAddMember={handleAddMember}
        onRemoveMember={handleRemoveMember}
      />

      <div className="chat-window-controls">
        {isEdgeMode && (
          <button className="header-btn" onClick={handleMinimize} title="Minimize">
            −
          </button>
        )}
        {!isEdgeMode && (
          <button
            className="header-btn"
            onClick={toggleMaximizePanel}
            title={isMaximized ? "Restore" : "Maximize"}
          >
            {isMaximized ? "🗗" : "🗖"}
          </button>
        )}
        <button className="header-btn" onClick={handleClose} title="Close">
          ×
        </button>
      </div>

      <div className="chat-main">
        {isMailMode ? (
          <div className="mail-layout">
            <div className="mail-list-panel">

            </div>
            {/*  */}
            <div >
              {/* <div
  className="header-buttons"
  style={{
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '8px',
    padding: '8px',
 borderBottom: "1px solid rgba(226, 232, 240, 0.6)"
  }}
>
  {isEdgeMode && (
    <button className="header-btn" onClick={handleMinimize} title="Minimize">
      −
    </button>
  )}
  {!isEdgeMode && (
    <button
      className="header-btn"
      onClick={toggleMaximizePanel}
      title={isMaximized ? "Restore" : "Maximize"}
    >
      {isMaximized ? "🗗" : "🗖"}
    </button>
  )}
  <button className="header-btn" onClick={handleClose} title="Close">
    ×
  </button>
</div> */}

            </div>
            {/*  */}
            <div className="mail-detail-panel">
              <MailApp />
            </div>
          </div>
        ) : (
          <>
            <div className="chat-header">
              <div className="header-info">
                {isDMSearchActive ? (
                  <div className="dm-search-container">
                    <div className="dm-search-wrapper">
                      <input
                        type="text"
                        className="dm-search-input"
                        placeholder="Search users to add..."
                        value={dmSearchQuery}
                        onChange={(e) => setDmSearchQuery(e.target.value)}
                        autoFocus
                      />
                      <div className="dm-action-buttons">
                        <button className="dm-cancel-btn" onClick={handleCancelDMSelection}>
                          Cancel
                        </button>
                        {selectedDMs.length > 0 && (
                          <button className="dm-start-btn" onClick={handleStartConversation}>
                            Start ({selectedDMs.length})
                          </button>
                        )}
                      </div>
                    </div>

                    {selectedDMs.length > 0 && (
                      <div className="selected-dms">
                        {selectedDMs.map((dm) => (
                          <div key={dm.id} className="selected-dm-tag">
                            <span className="dm-name">{dm.name}</span>
                            <button className="remove-dm-btn" onClick={() => handleRemoveSelectedDM(dm.id)}>
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {dmSearchQuery && (
                      <div className="dm-dropdown">
                        {directMessages
                          .filter(
                            (dm) =>
                              dm.name.toLowerCase().includes(dmSearchQuery.toLowerCase()) &&
                              !selectedDMs.find((selected) => selected.id === dm.id)
                          )
                          .map((dm) => (
                            <div key={dm.id} className="dm-option" onClick={() => handleDMSelect(dm)}>
	                              <div className="dm-option-content">
	                                <div className="dm-option-avatar">
	                                  <img className="chat-avatar-img" src={getFreeAvatarUrl(dm.name)} alt="" />
	                                  <div className={`status-indicator ${getStatusClass(dm.status)}`}></div>
	                                </div>
                                <span className="dm-option-name">{dm.name}</span>
                                <span className={`dm-option-status ${dm.status}`}>{dm.status}</span>
                              </div>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="header-channel-info">
                    {currentChannelInfo?.isWelcome ? (
                      <div className="header-avatar-section">
                        <div className="header-text-info">
                          <h3 className="header-title">Welcome</h3>
                          <div className="header-status-text">
                            starting a conversation
                          </div>
                        </div>
                      </div>
	                    ) : directMessages.find((dm) => dm.id === activeChannel) ? (
	                      <div className="header-avatar-section">
	                        <div className="header-avatar">
	                          <img
	                            className="chat-avatar-img"
	                            src={getFreeAvatarUrl(directMessages.find((dm) => dm.id === activeChannel)?.name)}
	                            alt=""
	                          />
	                          <div
	                            className={`header-status-indicator ${getStatusClass(
	                              directMessages.find((dm) => dm.id === activeChannel)?.status
                            )}`}
                          ></div>
                        </div>
                        <div className="header-text-info">
                          <h3 className="header-title">
                            {directMessages.find((dm) => dm.id === activeChannel)?.name}
                          </h3>
                        </div>
                      </div>
                    ) : currentChannelInfo?.isGroup ? (
                      <div className="header-avatar-section">
                        <div className="header-avatar group-avatar">👥</div>
                        <div className="header-text-info">
                          <h3 className="header-title">{currentChannelInfo.name}</h3>
                          <div className="header-status-text">{currentChannelInfo.memberCount} members</div>
                        </div>
                      </div>
                    ) : (
                      <div className="channel-header-container">
                        <div className="channel-name-container">
                          <h3 className="header-title">{currentChannelInfo?.name || `# ${activeChannel}`}</h3>
                        </div>
                        <div
                          className="channel-members-container"
                          onClick={handleOpenChannelMembersModal}
                          style={{ cursor: "pointer" }}
                          title="Click to manage members"
                        >
                          {currentChannelInfo?.members_name?.length > 0 ? (
                            <>
	                              {currentChannelInfo.members_name.slice(0, 3).map((member, index) => (
	                                <div key={index} className="member-avatar" title={member}>
	                                  <img className="chat-avatar-img" src={getFreeAvatarUrl(member)} alt="" />
	                                </div>
	                              ))}
                              {currentChannelInfo.members_name.length > 3 && (
                                <div className="extra-members">
                                  +{currentChannelInfo.members_name.length - 3}
                                </div>
                              )}
                            </>
                          ) : (
                            <div className="added-members" style={{ scale: "1.15" }}>
                              +
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="messages-container">
              <div className="messages-list">
                {isLoadingMessages && (
                  <div className="loading-messages">
                    <div className="loading-indicator">Loading messages...</div>
                  </div>
                )}

                {filteredMessages.map((message) => (
	                  <div key={message.id} className={`message ${message.sender}`}>
	                    <div className="message-avatar">
	                      <img className="chat-avatar-img" src={getFreeAvatarUrl(getMessageAvatarSeed(message))} alt="" />
	                    </div>
                    <div className="message-content">
                      {message.sender === "other" && message.senderName && (
                        <div className="message-sender-header">
                          <span className="message-sender-name">{message.senderName}</span>
                          <span className="message-time-inline">{message.timestamp}</span>
                        </div>
                      )}

                      <div className="message-text">{message.text}</div>
                      {!(message.sender === "other" && message.senderName) && (
                        <div className="message-time">{message.timestamp}</div>
                      )}
                    </div>
                  </div>
                ))}

	                {isTyping && (
	                  <div className="message bot">
	                    <div className="message-avatar">
	                      <img className="chat-avatar-img" src={getFreeAvatarUrl("PaisaVara Assistant")} alt="" />
	                    </div>
                    <div className="message-content">
                      <div className="typing-indicator">
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                        <span className="typing-dot"></span>
                      </div>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </div>
            </div>

            <div className="chat-input-area">
              <div className="input-wrapper">
                <textarea
                  className="chat-input"
                  placeholder={
                    activeChannel === "welcome"
                      ? "Create a channel or start a conversation to begin messaging..."
                      : `Message ${currentChannelInfo?.name || activeChannel}...`
                  }
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  onKeyPress={handleKeyPress}
                  rows={1}
                  disabled={activeChannel === "welcome"}
                  style={{
                    resize: "none",
                    minHeight: "44px",
                    maxHeight: "120px",
                    overflow: "auto",
                    opacity: activeChannel === "welcome" ? 0.6 : 1,
                  }}
                />
                <button
                  className="send-btn"
                  onClick={handleSendMessage}
                  disabled={!inputValue.trim() || activeChannel === "welcome"}
                  title="Send message"
                  style={{
                    opacity: activeChannel === "welcome" ? 0.6 : 1,
                  }}
                >
                  ➤
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );

}

export default ChatPanel;
